import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Lock, AlertCircle } from "lucide-react";
import { Logo, GenisLogo } from "@/components/Logo";
import { MaskedLines, EASE } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";

const HERO_IMG = "/img/hero.jpg";

const MARQUEE_ITEMS = [
  "BAR-TH-137 — Raccordement réseau de chaleur",
  "Habitat collectif",
  "BAR-TH-179 — PAC air/eau",
  "Copropriétés",
  "BAR-TH-127 — VMC hygroréglable",
  "Zones H1 · H2 · H3",
  "Certificats d'économies d'énergie",
];

const Login = () => {
  const { login } = useAuth();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const orbY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await login(password);
      window.location.href = "/calculateur";
    } catch (err) {
      setError(apiErrorMessage(err));
      setShake(true);
      setTimeout(() => setShake(false), 600);
      setBusy(false);
    }
  };

  return (
    <div ref={containerRef} className="grain relative min-h-screen overflow-hidden bg-[#090D14]">
      <div className="grid lg:grid-cols-[1.15fr_1fr]">
        {/* ------- Colonne éditoriale ------- */}
        <div className="relative flex min-h-screen flex-col justify-between overflow-hidden p-8 sm:p-12 lg:p-16">
          <div className="grid-bg pointer-events-none absolute inset-0" />
          <motion.div
            style={{ y: orbY }}
            className="pointer-events-none absolute -left-32 top-1/3 h-[420px] w-[420px] rounded-full bg-sky-500/14 blur-[130px]"
          />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="relative z-10"
          >
            <Logo />
          </motion.div>

          <div className="relative z-10 max-w-2xl py-16">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mb-6 inline-flex items-center gap-2 rounded-full border border-sky-500/25 bg-sky-500/8 px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.24em] text-sky-300"
            >
              <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-sky-400" />
              Portail client — Certificats d'économies d'énergie
            </motion.p>
            <MaskedLines
              delay={0.35}
              className="font-display text-5xl font-extrabold leading-[1.02] tracking-tight text-slate-50 sm:text-6xl lg:text-7xl"
              lines={["L'efficacité", "énergétique,", "valorisée."]}
            />
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
              className="mt-7 max-w-xl text-[15px] leading-relaxed text-slate-400"
            >
              Calculez vos primes CEE sur les fiches standardisées <span className="font-mono text-sky-300">BAR-TH-137</span>,{" "}
              <span className="font-mono text-sky-300">BAR-TH-179</span> et{" "}
              <span className="font-mono text-sky-300">BAR-TH-127</span>, gérez vos dossiers opération par opération, comparez vos
              simulations et générez votre estimation — depuis un portail unique.
            </motion.p>
          </div>

          {/* Marquee éditorial */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="relative z-10 -mx-8 overflow-hidden border-t border-[#1E293B] py-4 sm:-mx-12 lg:-mx-16"
          >
            <div className="marquee-track">
              {[0, 1].map((dup) => (
                <div key={dup} aria-hidden={dup === 1} className="flex shrink-0 items-center">
                  {MARQUEE_ITEMS.map((item) => (
                    <span key={`${dup}-${item}`} className="flex items-center gap-6 pr-6 text-[11px] font-mono uppercase tracking-[0.22em] text-slate-500">
                      {item}
                      <span className="inline-block h-1 w-1 rotate-45 bg-sky-500/70" />
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ------- Colonne accès ------- */}
        <div className="relative flex min-h-[60vh] items-center justify-center overflow-hidden lg:min-h-screen">
          <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.4 }}>
            <img src={HERO_IMG} alt="Immeuble résidentiel" className="h-full w-full object-cover" />
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-l from-[#090D14]/55 via-[#090D14]/75 to-[#090D14]" />
          <div className="absolute inset-0 bg-sky-950/25 mix-blend-multiply" />
          <div className="absolute left-1/2 top-1/2 h-[380px] w-[380px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-sky-500/10 blur-[110px]" />

          <motion.div
            initial={{ opacity: 0, y: 34 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
            className={`relative z-10 w-[min(420px,90vw)] ${shake ? "shake" : ""}`}
          >
            <div className="glow-brand rounded-3xl border border-[#243044] p-8 sm:p-10" style={{ background: "rgba(8, 11, 18, 0.94)", backdropFilter: "blur(18px)" }}>
              <div className="mb-8 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-sky-500/30 bg-sky-500/10 text-sky-300">
                  <Lock size={17} />
                </span>
                <div>
                  <h2 className="font-display text-xl font-bold text-slate-50">Accès au portail</h2>
                  <p className="text-xs text-slate-500">Réservé à GROUPE CONNECT et ses partenaires</p>
                </div>
              </div>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label htmlFor="pw" className="mb-2 block text-[11px] font-mono uppercase tracking-widest text-slate-400">
                    Mot de passe
                  </label>
                  <input
                    id="pw"
                    data-testid="login-password-input"
                    type="password"
                    autoFocus
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••"
                    className="field-input text-lg tracking-widest"
                    style={{ background: "#000", color: "#F8FAFC", borderColor: "#2A3A52" }}
                  />
                </div>
                {error && (
                  <p data-testid="login-error" className="flex items-start gap-2 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-300">
                    <AlertCircle size={15} className="mt-0.5 shrink-0" />
                    {error}
                  </p>
                )}
                <button type="submit" data-testid="login-submit-btn" disabled={busy} className="btn-primary w-full py-3.5 text-[15px]">
                  {busy ? "Vérification…" : "Entrer dans le portail"}
                  {!busy && <ArrowRight size={16} />}
                </button>
              </form>
              <p className="mt-7 border-t border-[#1E293B] pt-5 text-[11px] leading-relaxed text-slate-500">
                Compte partenaire ou administrateur : un seul mot de passe suffit, le portail reconnaît votre rôle
                automatiquement.
              </p>
            </div>
          </motion.div>
          <div data-testid="login-footer-genis" className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2">
            <GenisLogo forceDark />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
